import { useState, useEffect } from 'react';

import { Box, Card, CardContent, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DragAndDropFilesUploader, HeaderWithStatus, StyledButton } from '~components';
import { formatBytes } from '~utils';

import { useStyles } from './style';
import { languages } from '../../../../../constantsOld';
import { isEmptyArray, isFullArray } from '../../../../../shared/utility';

interface UploadTermsAndConditionsProps {
	newFiles?: unknown[];
	dutch?: unknown[];
	german?: unknown[];
	english?: unknown[];
	save?(...args: unknown[]): unknown;
}

const UploadTermsAndConditions = (props: UploadTermsAndConditionsProps) => {
	const { save, newFiles, dutch, german, english } = props;
	const { t } = useTranslation();
	const classes = useStyles();

	const [fileName, setFileName] = useState(null);
	const [fileSize, setFileSize] = useState(null);
	const [selectedLanguage, setSelectedLanguage] = useState(null);
	const [files, setFiles] = useState([]);

	const handleLanguageChange = (event) => {
		event.preventDefault();
		setSelectedLanguage(event.target.value);
	};

	useEffect(() => {
		if (isFullArray(newFiles)) {
			save({ files: [] });
		}
	}, [newFiles]);

	const handleAddFile = () => {
		const saveableFiles = files.map((el) => ({ name: el.file.name, file: [el.file] }));
		save({
			selectedLanguage,
			fileName,
			fileSize,
			files: saveableFiles,
		});
		setFileName(null);
		setFileSize(null);
		setSelectedLanguage(null);
		setFiles([]);
	};

	return (
		<Box className={classes.root}>
			<Card className={classes.cardContainer}>
				<CardContent className={classes.cardContent}>
					<HeaderWithStatus
						header={t('views.addTerms.uploadFiles.title')}
						headerSize='h4'
						subheader={t('views.addTerms.uploadFiles.subtitle')}
						subheaderSize='body2'
					/>
					<TextField
						InputLabelProps={{
							shrink: true,
						}}
						SelectProps={{
							value: selectedLanguage || t('views.addTerms.uploadFiles.chooseLanguage'),
							displayEmpty: true,
							renderValue: (value) =>
								selectedLanguage ?
									t(`ui.${value}`)
								:	t('views.addTerms.uploadFiles.chooseLanguage'),
						}}
						className={classes.languageSelector}
						disabled={isFullArray(dutch) && isFullArray(german) && isFullArray(english)}
						fullWidth={true}
						onChange={handleLanguageChange}
						placeholder={t('views.addTerms.uploadFiles.chooseLanguage')}
						required
						select
						size='medium'
						variant='outlined'
					>
						{!isEmptyArray(languages) &&
							languages.map((ln) => (
								<MenuItem
									disabled={
										(isFullArray(dutch) && dutch[0].language === ln.language) ||
										(isFullArray(german) && german[0].language === ln.language) ||
										(isFullArray(english) && english[0].language === ln.language)
									}
									key={ln.code}
									value={ln.language}
								>
									{t(`ui.${ln.language}`)}
								</MenuItem>
							))}
					</TextField>
					<Typography className={classes.fileHeading} variant='h5'>
						{`${t('views.addTerms.uploadFiles.file')}`}
					</Typography>

					<DragAndDropFilesUploader
						accept='application/pdf'
						files={files}
						onChange={(vals) => {
							if (vals.length > 0) {
								const fileName = vals[0].file.name;
								setFileName(fileName);
								setFileSize(formatBytes(vals[0].file.size, 1));
							} else {
								setFileName(null);
								setFileSize(null);
							}
							setFiles(vals);
						}}
					/>

					<Box className={classes.buttons}>
						<StyledButton
							className={classes.addButton}
							disabled={!selectedLanguage || files.length <= 0}
							onClick={handleAddFile}
							size='large'
							variant='contained-secondary'
						>
							{t('ui.button.contained.add')}
						</StyledButton>
					</Box>
				</CardContent>
			</Card>
		</Box>
	);
};

export default UploadTermsAndConditions;
